// Manual forecast overrides. The ONLY place that talks to `forecast_overrides`.

import { supabase } from "../../lib/supabase";
import { overrideKey } from "../../lib/forecast/engine";

export interface ForecastOverride {
  customerId: string;
  hubId: string;
  date: string; // YYYY-MM-DD
  value: number;
  reason?: string;
}

interface OverrideRow {
  id: string;
  account_id: string;
  customer_id: string;
  hub_id: string;
  date: string;
  value: number;
  reason: string | null;
}

export function rowToOverride(row: OverrideRow): ForecastOverride {
  return {
    customerId: row.customer_id,
    hubId: row.hub_id,
    date: row.date,
    value: Number(row.value),
    reason: row.reason ?? undefined,
  };
}

export function overrideInputToRow(accountId: string, o: ForecastOverride) {
  return {
    account_id: accountId,
    customer_id: o.customerId,
    hub_id: o.hubId,
    date: o.date,
    value: o.value,
    reason: o.reason?.trim() || null,
  };
}

/** Build the Map the engine expects: overrideKey -> value. */
export function buildOverrideMap(overrides: ForecastOverride[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const o of overrides) m.set(overrideKey(o.customerId, o.hubId, o.date), o.value);
  return m;
}

/** Load all overrides for the account. */
export async function listAccountOverrides(accountId: string): Promise<ForecastOverride[]> {
  const { data, error } = await supabase
    .from("forecast_overrides")
    .select("*")
    .eq("account_id", accountId);
  if (error) throw error;
  return (data as OverrideRow[]).map(rowToOverride);
}

/** Upsert one override (one row per customer/hub/date). */
export async function saveOverride(accountId: string, o: ForecastOverride): Promise<void> {
  const { error } = await supabase
    .from("forecast_overrides")
    .upsert(overrideInputToRow(accountId, o), { onConflict: "customer_id,hub_id,date" });
  if (error) throw error;
}

/** Remove an override (revert to the computed value). Scoped by account. */
export async function deleteOverride(
  accountId: string,
  customerId: string,
  hubId: string,
  date: string
): Promise<void> {
  const { error } = await supabase
    .from("forecast_overrides")
    .delete()
    .eq("account_id", accountId)
    .eq("customer_id", customerId)
    .eq("hub_id", hubId)
    .eq("date", date);
  if (error) throw error;
}
